import { Component, Input, OnChanges } from '@angular/core';
import * as moment from 'moment';
import { interval, BehaviorSubject, Subscription, Observable } from 'rxjs';
import { AttributeValue } from '../../../services/dashboard/charts.types';
import { Finding, Range } from 'src/app/beans/health-summary/health-summary.module.bean';
import { ChartData } from 'src/app/beans/dashboard/dashboard-module.bean';
import { HealthSummaryService } from '../../../services/health-summary/health-summary.service';

/**
 * Wellness panel component
 */
@Component({
	selector: 'app-wellness-panel',
	templateUrl: './wellness-panel.component.html',
	styleUrls: ['./wellness-panel.component.scss'],
})
export class WellnessPanelComponent implements OnChanges {
	/** Holds the attributes whose values has to be displayed in summary */
	attributesList: string[] = ['BMI', 'Blood Pressure', 'Cholesterol', 'Diabetes', 'Exercise Status', 'Happiness'];
	/** Holds observable object of attributes */
	attributesListObservable: BehaviorSubject<string[]>;
	/** Holds attribute value */
	attributeValue: AttributeValue = null;
	/** Holds observable source */
	source: Observable<number> = interval(6000);
	/** Holds list of subscription */
	subscription: Subscription;
	/** Holds chart data */
	chartData: ChartData;
	/** To check if member is matched */
	@Input() isMatchedMember: boolean;
	/** Holds dashboard state object */
	@Input() panelStatus: any;
	/** Holds health summary data */
	healthData: Finding[] = [];
	/** Boolean to check if health data is available */
	isHealthDataAvailable: boolean;

	/**
	 * Wellness panel constructor
	 */
	constructor(private healthSummaryService: HealthSummaryService) {
		this.isHealthDataAvailable = false;
	}

	/**
	 * On component changes
	 */
	ngOnChanges(): void {
		this.getHealthSummary();
	}

	/**
	 * Builds summary object to be rendered
	 * @param healthData - summary
	 */
	observableBuilder(healthData: Finding[]): void {
		const newAttList: string[] = healthData
			.filter(
				(finding: Finding) =>
					this.attributesList.includes(finding.name) ||
					finding.name === 'Total Cholesterol' ||
					finding.name === 'Hemoglobin-A1C' ||
					finding.name === 'Happiness Scale'
			)
			.filter((finding: Finding) => finding.value !== 'Not Available' && finding.value !== 'Not Answered')
			.filter((finding: Finding) => finding.ranges.length > 0)
			.map((finding: Finding) => {
				if (finding.name === 'Total Cholesterol') {
					return 'Cholesterol';
				} else if (finding.name === 'Hemoglobin-A1C') {
					return 'Diabetes';
				} else if (finding.name === 'Happiness Scale') {
					return 'Happiness';
				} else {
					return finding.name;
				}
			});
		try {
			if (newAttList && newAttList.length > 0 && newAttList !== this.attributesList) {
				this.isHealthDataAvailable = true;
				this.attributesList = newAttList;
				this.attributesListObservable = new BehaviorSubject(this.attributesList);
				this.attributesListObservable.subscribe(attributesList => this.attributeListCheck(healthData, attributesList));
			}
		} catch (Exception) {
			console.error(`[${WellnessPanelComponent.name}][${this.observableBuilder.name}]`, Exception);
		}
	}

	/**
	 * Checks if data is available for each attribute and maps the same
	 */
	attributeListCheck(healthData: Finding[], attributesList: string[]): void {
		let metric: Finding;
		let chartType: string;
		switch (attributesList[0]) {
			case 'BMI':
				metric = healthData.filter(attribute => attribute.name === 'BMI')[0];
				chartType = 'calculated';
				break;
			case 'Blood Pressure':
				metric = healthData.filter(attribute => attribute.name === 'Blood Pressure')[0];
				chartType = 'categorical';
				break;
			case 'Cholesterol':
				metric = healthData.filter(attribute => attribute.name === 'Total Cholesterol')[0];
				chartType = 'calculated';
				break;
			case 'Diabetes':
				metric = healthData.filter(attribute => attribute.name === 'Hemoglobin-A1C')[0];
				chartType = 'calculated';
				break;
			case 'Exercise Status':
				metric = healthData.filter(attribute => attribute.name === 'Exercise Status')[0];
				chartType = 'text';
				break;
			case 'Happiness':
				metric = healthData.filter(attribute => attribute.name === 'Happiness Scale')[0];
				chartType = 'calculated';
				break;
		}
		metric.ranges.sort((a, b) => {
			return a.max - b.max;
		});
		let indicatorScale: Range = metric.ranges.filter(scale => scale.indicator === true)[0];
		// FIXME: this is here as a workaround
		// see FITEHEME-612 for more details
		if (indicatorScale === null || indicatorScale === undefined) {
			indicatorScale = metric.ranges[0];
		}
		const attributeDescription: string = indicatorScale.desc;
		const attributeScale: number[][] = metric.ranges.map(scale => [scale.min, scale.max]);
		const attributeUpdatedDate: string = moment(metric.lastUpdated, 'MM-DD-YYYY').format('MMM D, YYYY');
		const attributeScalesColors: string[] = metric.ranges.map(scale => scale.hexValue);
		const attributeValue: string = metric.value;
		const chartName: string = metric.name;
		const metricDescription: string = metric.description;
		// const metricDescription: string = metric.description?.replace(/|/ig, `"`);
		const pointerColor: string = indicatorScale.hexValue;
		const unitOfMeasurement: string = metric.unitOfMeasurement;
		this.chartData = {
			attributeDescription,
			attributeScale,
			attributeUpdatedDate,
			attributeValue,
			chartDisplay: 'single',
			chartName,
			chartType,
			colors: attributeScalesColors,
			metricDescription,
			pointerColor,
			unitOfMeasurement,
		};
	}

	/**
	 * Array Shiffling
	 * @description Changes the Attribute order based on the index
	 * @param {number} i the index
	 * @memberof WellnessPanelComponent
	 */
	ArrayShuffle(i: number): void {
		if (this.attributesList !== undefined && this.attributesList !== null && this.attributesList.length > 1) {
			const removedElements: string[] = this.attributesList.slice(0, i);
			const beginingArray: string[] = this.attributesList.slice(i);
			const combinedArray: string[] = beginingArray.concat(removedElements);
			this.attributesList = combinedArray;
			this.attributesListObservable.next(combinedArray);
		}
	}

	/**
	 * To unsubscribe when mouse enter event occuers
	 */
	mouseEnter(): void {
		if (this.subscription) {
			this.subscription.unsubscribe();
		}
	}

	/**
	 * To subscribe when mouse leave event occuers
	 */
	mouseLeave(): void {
		if (this.subscription) {
			this.subscription = this.source.subscribe(val => this.ArrayShuffle(1));
		}
	}

	/**
	 *
	 * retreives the wellness / health portfolio data for a given user
	 * @private
	 * @memberof HeaderComponent
	 */
	private getHealthSummary(): void {
		this.healthSummaryService.getHealthSummary().subscribe(
			healthData => {
				this.healthData = healthData.data;
				if (this.healthData !== undefined && this.healthData !== null && this.healthData.length > 0) {
					this.observableBuilder(this.healthData);
				}
				if (this.isHealthDataAvailable) {
					this.subscription = this.source.subscribe(val => this.ArrayShuffle(1));
				}
			},
			err => {
				console.error(`[${WellnessPanelComponent.name}][${this.getHealthSummary.name}]`, 'An error occurred while fetching summary ', err);
			}
		);
	}
}
